import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from 'contexts/AuthContext';
import userService from 'services/UserService';
import { logger } from '../../../utils/logger';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Button,
  Alert,
  Tabs,
  Tab,
  Chip,
  Fade,
  useTheme,
  alpha,
  Tooltip,
  Badge
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import UpdateIcon from '@mui/icons-material/Update';
import Breadcrumb from 'components/Breadcrumb';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

// Create a module-specific logger
const log = logger.getLogger('UserProfilePage');

// User details interface
interface UserDetails {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  loading: boolean;
  error?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const UserProfilePage: React.FC = () => {
  const theme = useTheme();
  // Get user data directly from Auth0
  const { user: auth0User, isLoading: auth0Loading } = useAuth0();
  // Also get user data from our AuthContext which has been mapped
  const { auth, refreshUserProfile } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // New state for created by and updated by user details
  const [createdByUser, setCreatedByUser] = useState<UserDetails | null>(null);
  const [updatedByUser, setUpdatedByUser] = useState<UserDetails | null>(null);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Function to fetch user details by ID
  const fetchUserDetails = async (userId: string, setUser: React.Dispatch<React.SetStateAction<UserDetails | null>>) => {
    if (!userId) return;
    
    try {
      setUser({
        id: userId,
        firstName: '',
        lastName: '',
        loading: true
      });
      
      const userProfile = await userService.getUserProfileById(userId);
      
      setUser({
        id: userId,
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email,
        loading: false
      });
    } catch (err) {
      log.error(`Error fetching user details for ID ${userId}:`, err);
      setUser({
        id: userId,
        firstName: '',
        lastName: '',
        loading: false,
        error: 'Failed to load user details'
      });
    }
  };

  const handleRefreshProfile = useCallback(async () => {
    if (auth.user?.sub) {
      try {
        setLoading(true);
        setError(null);
        // Extract the actual ID from the sub claim (format: auth0|12345)
        const idParts = auth.user.sub.split('|');
        const userId = idParts.length > 1 ? idParts[1] : auth.user.sub;
        await refreshUserProfile(userId);
      } catch (err) {
        log.error('Error refreshing profile:', err);
        setError('Failed to refresh profile data. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [auth.user, refreshUserProfile, setLoading, setError]);
  
  // Fetch account profile on component mount if not already loaded
  useEffect(() => {
    if (auth.user?.sub && !auth.user.accountProfile) {
      handleRefreshProfile();
    }
  }, [auth.user?.sub, auth.user?.accountProfile, handleRefreshProfile]);
  
  // Fetch creator and updater information when profile is loaded
  useEffect(() => {
    if (auth.user?.accountProfile) {
      const { createdBy, updatedBy } = auth.user.accountProfile;
      
      if (createdBy && !createdByUser) {
        fetchUserDetails(createdBy.toString(), setCreatedByUser);
      }
      
      if (updatedBy && !updatedByUser) {
        fetchUserDetails(updatedBy.toString(), setUpdatedByUser);
      }
    }
  }, [auth.user?.accountProfile, createdByUser, updatedByUser]);
  
  // Format user display information
  const formatUserInfo = (user: UserDetails | null) => {
    if (!user) return 'N/A';
    if (user.loading) return 'Loading...';
    if (user.error) return `Unknown User (${user.id})`;
    
    return `${user.firstName} ${user.lastName} (${user.id})`;
  };

  if (auth0Loading || auth.isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
        </Box>
      </Container>
    );
  }

  if (!auth0User || !auth.isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Fade in={true} timeout={800}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <Typography color="error" variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <CancelIcon sx={{ mr: 1 }} /> Unable to load profile information. Please try logging in again.
            </Typography>
          </Paper>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
      <Breadcrumb 
        items={[
          {
            label: 'User Profile',
            icon: <PersonOutlineIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          }
        ]}
      />
      
      <Fade in={true} timeout={800}>
        <Box>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.primary.main,
              mb: 3
            }}
          >
            User Profile
          </Typography>
          
          <Paper 
            elevation={2} 
            sx={{ 
              borderRadius: 3, 
              overflow: 'hidden',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 8px 24px 0 rgba(0,0,0,0.1)'
              }
            }}
          >
            <Box 
              sx={{ 
                p: 3, 
                background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
              }}
            >
              <Box display="flex" alignItems="center">
                {auth0User.picture ? (
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      auth0User.email_verified ? (
                        <Tooltip title="Verified User">
                          <VerifiedUserIcon 
                            fontSize="small" 
                            color="primary" 
                            sx={{ 
                              backgroundColor: 'white', 
                              borderRadius: '50%',
                              padding: 0.2,
                              width: 24,
                              height: 24
                            }} 
                          />
                        </Tooltip>
                      ) : null
                    }
                  >
                    <Avatar 
                      src={auth0User.picture} 
                      alt={auth0User.name}
                      sx={{ 
                        width: 90, 
                        height: 90, 
                        mr: 3,
                        border: `3px solid ${theme.palette.background.paper}`,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Badge>
                ) : (
                  <Avatar 
                    sx={{ 
                      width: 90, 
                      height: 90, 
                      mr: 3,
                      bgcolor: theme.palette.primary.main,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                )}
                <Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    {auth0User.name}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <EmailIcon fontSize="small" sx={{ mr: 0.7, color: alpha(theme.palette.text.primary, 0.6) }} />
                    <Typography variant="body1" sx={{ color: alpha(theme.palette.text.primary, 0.8) }}>
                      {auth0User.email}
                    </Typography>
                  </Box>
                  
                  {auth.user?.accountProfile && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                      {auth.user.accountProfile.roles?.map((r) => (
                            <Chip
                              key={r.name}
                              label={r.displayName || r.name}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 500, borderRadius: '8px' }}
                            />
                          ))
                        }
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mx: 3, 
                  mt: 3, 
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    alignItems: 'center'
                  }
                }}
              >
                {error}
              </Alert>
            )}
            
            <Box sx={{ px: 3, pt: 2 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="user profile tabs"
                sx={{
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    minWidth: 100,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      opacity: 1
                    },
                    '&.Mui-selected': {
                      color: theme.palette.primary.main
                    }
                  }
                }}
              >
                <Tab label="Basic Info" id="user-tab-0" aria-controls="user-tabpanel-0" />
                <Tab label="Account Profile" id="user-tab-1" aria-controls="user-tabpanel-1" />
              </Tabs>
            </Box>
            
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ px: 3, pb: 2 }}>
                <Box 
                  sx={{ 
                    mb: 3,
                    pb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: theme.palette.primary.main
                    }}
                  >
                    Profile Details
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: alpha(theme.palette.text.primary, 0.6),
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <UpdateIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Last Updated: {auth0User.updated_at ? new Date(auth0User.updated_at).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
                
                <Table sx={{ minWidth: '100%' }}>
                  <TableBody 
                    sx={{
                      '& .MuiTableRow-root:nth-of-type(odd)': {
                        backgroundColor: alpha(theme.palette.background.default, 0.4),
                      }
                    }}
                  >
                    {/* Auth0 ID */}
                    <TableRow
                      sx={{ 
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.light, 0.05)
                        }
                      }}
                    >
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                          pl: 2, 
                          pr: 2, 
                          width: '30%', 
                          border: 'none',
                          verticalAlign: 'top',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Auth0 ID</Typography>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2, 
                          border: 'none',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="body2">
                          {auth0User.sub}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Nickname */}
                    <TableRow
                      sx={{ 
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.light, 0.05)
                        }
                      }}
                    >
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                          pl: 2, 
                          pr: 2, 
                          width: '30%', 
                          border: 'none',
                          verticalAlign: 'top',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Nickname</Typography>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2, 
                          border: 'none',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="body2">
                          {auth0User.nickname || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Email */}
                    <TableRow
                      sx={{ 
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.light, 0.05)
                        }
                      }}
                    >
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                          pl: 2, 
                          pr: 2, 
                          width: '30%', 
                          border: 'none',
                          verticalAlign: 'top',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Email</Typography>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2, 
                          border: 'none',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="body2">
                          {auth0User.email}
                        </Typography>
                        {auth0User.email_verified && (
                          <Chip 
                            size="small"
                            icon={<VerifiedUserIcon sx={{ fontSize: '0.9rem !important' }} />}
                            label="Verified"
                            color="success"
                            variant="outlined"
                            sx={{ 
                              mt: 0.5,
                              height: 24,
                              fontSize: '0.75rem',
                              '& .MuiChip-icon': { ml: 0.5 }
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Last Updated */}
                    <TableRow
                      sx={{ 
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.light, 0.05)
                        }
                      }}
                    >
                      <TableCell 
                        component="th" 
                        scope="row" 
                        sx={{ 
                          pl: 2, 
                          pr: 2, 
                          width: '30%', 
                          border: 'none',
                          verticalAlign: 'top',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Last Updated</Typography>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2, 
                          border: 'none',
                          pt: 2,
                          pb: 2
                        }}
                      >
                        <Typography variant="body2">
                          {auth0User.updated_at ? new Date(auth0User.updated_at).toLocaleString() : 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Box sx={{ px: 3, pb: 2 }}>
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center" 
                  mb={3}
                  pb={1}
                  borderBottom={`1px solid ${alpha(theme.palette.divider, 0.3)}`}
                >
                  <Box display="flex" alignItems="center">
                    <BusinessIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: theme.palette.primary.main
                      }}
                    >
                      Account Profile
                    </Typography>
                  </Box>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    size="small"
                    onClick={handleRefreshProfile} 
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 3px 8px rgba(0,0,0,0.08)'
                      }
                    }}
                  >
                    {loading ? 'Refreshing...' : 'Refresh Profile Data'}
                  </Button>
                </Box>
                
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress size={50} thickness={4} />
                  </Box>
                ) : auth.user?.accountProfile ? (
                  <Table sx={{ minWidth: '100%' }}>
                    <TableBody 
                      sx={{
                        '& .MuiTableRow-root:nth-of-type(odd)': {
                          backgroundColor: alpha(theme.palette.background.default, 0.4),
                        }
                      }}
                    >
                      {/* Account ID */}
                      <TableRow
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            pl: 2, 
                            pr: 2, 
                            width: '30%', 
                            border: 'none',
                            verticalAlign: 'top',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Account ID</Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2, 
                            border: 'none',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="body2">
                            {auth.user.accountProfile.id || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      
                      {/* User Name */}
                      {(auth.user.accountProfile.username) && (
                        <TableRow
                          sx={{ 
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.light, 0.05)
                            }
                          }}
                        >
                          <TableCell 
                            component="th" 
                            scope="row" 
                            sx={{ 
                              pl: 2, 
                              pr: 2, 
                              width: '30%', 
                              border: 'none',
                              verticalAlign: 'top',
                              pt: 2,
                              pb: 2
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Username</Typography>
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              pl: 2, 
                              border: 'none',
                              pt: 2,
                              pb: 2
                            }}
                          >
                            <Typography variant="body2">
                              {`${auth.user.accountProfile.username || ''}`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Roles */}
                      <TableRow
                        sx={{
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ pl: 2, pr: 2, width: '30%', border: 'none', verticalAlign: 'top', pt: 2, pb: 2 }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Roles</Typography>
                        </TableCell>
                        <TableCell sx={{ pl: 2, border: 'none', pt: 2, pb: 2 }}>
                          {auth.user.accountProfile.roles && auth.user.accountProfile.roles.length > 0
                            ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {auth.user.accountProfile.roles.map((r) => (
                                    <Chip
                                      key={r.name}
                                      label={r.displayName || r.name}
                                      size="small"
                                      color="primary"
                                      sx={{ fontWeight: 500, borderRadius: '8px' }}
                                    />
                                  ))}
                                </Box>
                              )
                            : (
                                <Typography variant="body2" color="text.secondary">None</Typography>
                              )
                          }
                        </TableCell>
                      </TableRow>

                      {/* Account Name */}
                      <TableRow
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            pl: 2, 
                            pr: 2, 
                            width: '30%', 
                            border: 'none',
                            verticalAlign: 'top',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Account Name</Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2, 
                            border: 'none',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: theme.palette.primary.main
                            }}
                          >
                            {auth.user.accountProfile.account.name || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Listen Reporting */}
                      <TableRow
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            pl: 2, 
                            pr: 2, 
                            width: '30%', 
                            border: 'none',
                            verticalAlign: 'top',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Listen Reporting</Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2, 
                            border: 'none',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          {auth.user.accountProfile.account.listenPortalEnabled || false ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Enabled"
                              size="small"
                              color="success"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: '8px',
                                '& .MuiChip-icon': { ml: 0.5 }
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<CancelIcon />}
                              label="Disabled"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: '8px',
                                '& .MuiChip-icon': { ml: 0.5 }
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Listen Reporting Only */}
                      <TableRow
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            pl: 2, 
                            pr: 2, 
                            width: '30%', 
                            border: 'none',
                            verticalAlign: 'top',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Listen Reporting Only</Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2, 
                            border: 'none',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          {auth.user.accountProfile.account.listenReportingOnly || false ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Enabled"
                              size="small"
                              color="success"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: '8px',
                                '& .MuiChip-icon': { ml: 0.5 }
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<CancelIcon />}
                              label="Disabled"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: '8px',
                                '& .MuiChip-icon': { ml: 0.5 }
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Strategy Edge */}
                      <TableRow
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            pl: 2, 
                            pr: 2, 
                            width: '30%', 
                            border: 'none',
                            verticalAlign: 'top',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Strategy Edge</Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2, 
                            border: 'none',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          {auth.user.accountProfile.account.strategyPortalEnabled || false ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Enabled"
                              size="small"
                              color="success"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: '8px',
                                '& .MuiChip-icon': { ml: 0.5 }
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<CancelIcon />}
                              label="Disabled"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ 
                                fontWeight: 500,
                                borderRadius: '8px',
                                '& .MuiChip-icon': { ml: 0.5 }
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                      
                      {/* Strategy Portal Enabled */}
                      {auth.user.accountProfile.account.strategyPortalEnabled !== undefined && (
                        <TableRow
                          sx={{ 
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.light, 0.05)
                            }
                          }}
                        >
                          <TableCell 
                            component="th" 
                            scope="row" 
                            sx={{ 
                              pl: 2, 
                              pr: 2, 
                              width: '30%', 
                              border: 'none',
                              verticalAlign: 'top',
                              pt: 2,
                              pb: 2
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                              Strategy Portal Enabled
                            </Typography>
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              pl: 2, 
                              border: 'none',
                              pt: 2,
                              pb: 2
                            }}
                          >
                            {auth.user.accountProfile.account.strategyPortalEnabled ? (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Yes"
                                size="small"
                                color="success"
                                sx={{ 
                                  fontWeight: 500,
                                  borderRadius: '8px',
                                  '& .MuiChip-icon': { ml: 0.5 }
                                }}
                              />
                            ) : (
                              <Chip
                                icon={<CancelIcon />}
                                label="No"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ 
                                  fontWeight: 500,
                                  borderRadius: '8px',
                                  '& .MuiChip-icon': { ml: 0.5 }
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {/* Created By */}
                      <TableRow
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            pl: 2, 
                            pr: 2, 
                            width: '30%', 
                            border: 'none',
                            verticalAlign: 'top',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Created By</Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2, 
                            border: 'none',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="body2">
                            {formatUserInfo(createdByUser)}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Updated By */}
                      <TableRow
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.light, 0.05)
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            pl: 2, 
                            pr: 2, 
                            width: '30%', 
                            border: 'none',
                            verticalAlign: 'top',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.secondary">Updated By</Typography>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2, 
                            border: 'none',
                            pt: 2,
                            pb: 2
                          }}
                        >
                          <Typography variant="body2">
                            {formatUserInfo(updatedByUser)}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Other properties */}
                      {Object.entries(auth.user.accountProfile).map(([key, value]) => {
                        // Skip properties already shown or that are objects/arrays
                        if (['id', 'name', 'firstName', 'lastName', 'role', 'username', 'lineOfBusiness', 'strategyPortalEnabled', 'createdBy', 'updatedBy', 'account'].includes(key) || 
                            typeof value === 'object' || Array.isArray(value)) {
                          return null;
                        }
                        
                        return (
                          <TableRow
                            key={key}
                            sx={{ 
                              transition: 'all 0.2s',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.light, 0.05)
                              }
                            }}
                          >
                            <TableCell 
                              component="th" 
                              scope="row" 
                              sx={{ 
                                pl: 2, 
                                pr: 2, 
                                width: '30%', 
                                border: 'none',
                                verticalAlign: 'top',
                                pt: 2,
                                pb: 2
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                                {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                              </Typography>
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                pl: 2, 
                                border: 'none',
                                pt: 2,
                                pb: 2
                              }}
                            >
                              {typeof value === 'boolean' ? (
                                value ? (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Yes"
                                    size="small"
                                    color="success"
                                    sx={{ 
                                      fontWeight: 500,
                                      borderRadius: '8px',
                                      '& .MuiChip-icon': { ml: 0.5 }
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    icon={<CancelIcon />}
                                    label="No"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    sx={{ 
                                      fontWeight: 500,
                                      borderRadius: '8px',
                                      '& .MuiChip-icon': { ml: 0.5 }
                                    }}
                                  />
                                )
                              ) : (
                                <Typography variant="body2">
                                  {value?.toString() || 'N/A'}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        alignItems: 'center'
                      }
                    }}
                  >
                    No account profile data is available. Click "Refresh Profile Data" to load your account details.
                  </Alert>
                )}
              </Box>
            </TabPanel>
          </Paper>
        </Box>
      </Fade>
    </Container>
  );
};

export default UserProfilePage;